// ** Modules
import React from 'react';
import { signOut } from 'next-auth/react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import { HeaderLogo, HeaderMenuButton } from '@/components/common/HeaderMenuButton';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Utils
import { logoSwitcher } from '@/utils/ui/styleUtils';

// ** Types
import type { User } from 'next-auth';
import type { AppContext } from '@/types/contextTypes';
import type { LayoutProps } from '@/types/layoutProps';
import type { SxProps, Theme } from '@mui/material/styles';

type HorizontalLayout = NonNullable<LayoutProps['horizontalLayoutProps']>;
type AppBar = NonNullable<HorizontalLayout['appBar']>;

export type NavbarProps = {
	logoAlt?: string;
	showMenu?: boolean;
	menuLabel?: string;
	logoutLabel?: string;
	variant?: 'dark' | 'light';
	hideOnScroll?: boolean;
	scrollThresholdPx?: number;
	minDeltaPx?: number;
	hideOnMobile?: boolean;
	positionFixed?: boolean;
	containerMaxWidthPx?: number;
	containerDisableGutters?: boolean;
	containerSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	path?: string;
	hidden?: LayoutProps['hidden'];
	settings: LayoutProps['settings'];
	saveSettings: LayoutProps['saveSettings'];
	appBarContent: AppBar['content'];
	appBarProps: AppBar['componentProps'];
	currentUser: User;
	host: AppContext['host'];
	isAtTop?: boolean;
	onMenuClick?: () => void;
};

const HorizontalAppBarContent = ({
	onMenuClick,
	logoAlt = 'Logo',
	menuLabel = 'Menu',
	logoutLabel = 'Logout',
	host,
	isAtTop,
	settings,
	currentUser
}: NavbarProps) => {
	const theme = useTheme();
	const { override } = useHeaderOverride();

	// Context driven variables
	const activeVariant = override?.appBarVariant ?? settings.appBarVariant;
	const activeShowMenu = override?.showMenu ?? settings.showMenu;
	const shouldShowMenuButton = (override ? activeShowMenu : true) || !isAtTop;

	// The navbar background is transparent only at the very top of a `dark`
	// hero; in every other state it sits on a dark blur, so the content is white.
	const useWhiteContent = !isAtTop || activeVariant !== 'light';
	const contentColor = useWhiteContent
		? theme.palette.common.white
		: (theme.palette.text.secondary ?? theme.palette.text.primary);

	const isLoggedIn = Boolean(currentUser);

	const handleLogout = () => {
		signOut({ callbackUrl: '/' });
	};

	// Logo based on host
	const logo = host ? logoSwitcher(host) : '/images/drafts/logo.png';

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				transition: 'height 220ms ease',
				willChange: 'height',
				height: {
					xs: `${24 + 50 + 16 + (isAtTop ? 0 : -12)}px`,
					md: `${24 + 64 + 16 + (isAtTop ? 0 : -12)}px`
				}
			}}
		>
			<Link href="/" passHref>
				<HeaderLogo
					logoSrc={logo}
					logoAlt={logoAlt}
					sx={{
						position: 'relative',
						top: 'auto',
						left: 'auto',
						width: { xs: 56, md: 72 },
						height: { xs: 50, md: 64 }
					}}
				/>
			</Link>

			<Box
				sx={{
					display: { xs: 'none', sm: 'flex' },
					alignItems: 'center',
					gap: '16px'
				}}
			>
				{isLoggedIn ? (
					<React.Fragment>
						<Box
							component="button"
							type="button"
							onClick={handleLogout}
							sx={{
								display: 'flex',
								alignItems: 'center',
								border: 0,
								background: 'transparent',
								padding: 0,
								cursor: 'pointer',
								'@media (hover: hover) and (pointer: fine)': {
									'&:hover': { opacity: 0.7 }
								}
							}}
						>
							<Typography variant="body-md" sx={{ color: contentColor, textAlign: 'right' }}>
								{logoutLabel}
							</Typography>
						</Box>

						<Box
							aria-hidden
							sx={{
								width: '1px',
								height: '32px',
								background: alpha(useWhiteContent ? theme.palette.common.white : theme.palette.common.black, 0.18)
							}}
						/>
					</React.Fragment>
				) : null}

				<HeaderMenuButton
					onClick={onMenuClick}
					show={shouldShowMenuButton}
					label={menuLabel}
					color={contentColor}
					showOnMobile
					hideLabelOnMobile={false}
					sx={{
						position: 'relative',
						top: 'auto',
						right: 'auto',
						display: 'flex'
					}}
				/>
			</Box>
		</Box>
	);
};

export default HorizontalAppBarContent;
